
import React, { useState, useEffect } from 'react';
import { LazyImage } from '../../../components/LazyImage';

interface ProductImageHeaderProps {
    imageUrl: string;
    images?: string[]; // Array of images
    title: string;
    toggleFavorite: () => void;
    isFav: boolean;
    isOutOfStock: boolean;
}

export const ProductImageHeader = React.memo(({ 
    imageUrl, 
    images,
    title, 
    toggleFavorite, 
    isFav, 
    isOutOfStock 
}: ProductImageHeaderProps) => {
    const [isZoomed, setIsZoomed] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    
    // Normalize images: use array if present, otherwise fallback to single image
    // Ensure the main 'imageUrl' is the first one if array provided
    const gallery = images && images.length > 0 ? images : [imageUrl];
    
    // Reset index if product changes
    useEffect(() => {
        setActiveIndex(0);
    }, [imageUrl, title]); // Re-run if image or title changes (better proxy for product change)

    const currentImage = gallery[activeIndex];

    return (
        <>
            <div className="relative w-full h-[55vh] bg-background-light dark:bg-surface-dark overflow-hidden group">
                 {/* Main Image Layer */}
                 <div 
                    onClick={() => setIsZoomed(true)}
                    className="w-full h-full will-change-transform cursor-zoom-in relative" 
                    style={{ transform: `translate3d(0, calc(var(--scroll-y-prod, 0px) * 0.5), 0)` }}
                 >
                     <LazyImage 
                        key={currentImage} // Force fresh mount for each image to avoid flicker/blank state
                        src={currentImage} 
                        alt={`${title} - view ${activeIndex + 1}`} 
                        className="w-full h-full object-cover animate-fade-in" 
                        containerClassName="w-full h-full" 
                        fetchPriority="high" 
                    />
                 </div>
                 
                 <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/50 to-transparent pointer-events-none"></div>
                 <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
                 
                 {/* Zoom Button - Always Visible but sleek */}
                 <button 
                    onClick={() => setIsZoomed(true)}
                    className="absolute top-24 right-6 w-12 h-12 bg-white/10 dark:bg-black/20 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95 z-30 shadow-lg"
                 >
                    <span className="material-symbols-outlined text-2xl">zoom_in</span>
                 </button>

                 {/* Gallery Thumbnails - Floating Bottom Left */}
                 {gallery.length > 1 && (
                     <div className="absolute bottom-6 left-6 z-30 flex gap-2 overflow-x-auto no-scrollbar max-w-[calc(100%-80px)] p-1">
                         {gallery.map((img, idx) => (
                             <button
                                key={idx}
                                onClick={(e) => { e.stopPropagation(); setActiveIndex(idx); }}
                                className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-300 shadow-lg ${
                                    activeIndex === idx 
                                        ? 'border-primary scale-110' 
                                        : 'border-white/30 opacity-70 hover:opacity-100 hover:border-white'
                                }`}
                             >
                                 <img src={img} alt={`Imagen ${idx + 1} de ${title || 'producto'}`} className="w-full h-full object-cover" />
                             </button>
                         ))}
                     </div>
                 )}

                 <button 
                    onClick={toggleFavorite} 
                    className="absolute bottom-6 right-6 w-14 h-14 bg-white/20 dark:bg-black/30 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95 z-30 shadow-lg group"
                 >
                    <span className={`material-symbols-outlined text-3xl transition-transform group-active:scale-90 ${isFav ? 'text-red-500 filled' : 'text-white'}`}>favorite</span>
                 </button>
                 
                 {isOutOfStock && (
                     <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 backdrop-blur-sm z-20 pointer-events-none">
                         <span className="bg-black text-white px-6 py-3 text-xl font-bold rounded-full transform -rotate-12 shadow-2xl border border-white/20">AGOTADO</span>
                     </div>
                 )}
            </div>

            {/* ZOOM MODAL */}
            {isZoomed && (
                <div 
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl animate-fade-in flex flex-col items-center justify-center touch-none"
                    onClick={() => setIsZoomed(false)}
                >
                    <div className="relative w-full h-full flex items-center justify-center p-4">
                        <img 
                            src={currentImage} 
                            alt={title} 
                            className="max-w-full max-h-full object-contain animate-scale-up" 
                        />
                    </div>
                    
                    {/* Gallery dots in zoom mode */}
                    {gallery.length > 1 && (
                        <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-3 p-4 bg-gradient-to-t from-black/50 to-transparent" onClick={(e) => e.stopPropagation()}>
                            {gallery.map((_, idx) => (
                                <button 
                                    key={idx}
                                    onClick={() => setActiveIndex(idx)}
                                    className={`w-3 h-3 rounded-full transition-all duration-300 ${activeIndex === idx ? 'bg-primary w-8' : 'bg-white/30'}`}
                                ></button>
                            ))}
                        </div>
                    )}

                    <button 
                        onClick={() => setIsZoomed(false)}
                        className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white backdrop-blur-xl border border-white/20 transition-all active:scale-90"
                    >
                        <span className="material-symbols-outlined text-2xl">close</span>
                    </button>
                </div>
            )}
        </>
    );
});
ProductImageHeader.displayName = 'ProductImageHeader';
